import React, { useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useUsage } from '../../hooks/useUsage';
import { useAuthFetch } from '../../hooks/useAuthFetch';

const IconBuilding = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><line x1="8" y1="6" x2="10" y2="6"/><line x1="14" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="10" y2="10"/><line x1="14" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="14" y1="14" x2="16" y2="14"/></svg>
);
const IconCreditCard = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
);
const IconCode = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
);
const IconToggle = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="5" width="22" height="14" rx="7" ry="7"/><circle cx="16" cy="12" r="3"/></svg>
);
const IconLayers = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
);

export const SettingsPage: React.FC = () => {
  const { org, role, canPerform, refresh } = useTenant();
  const { usage } = useUsage();
  const authFetch = useAuthFetch();
  const [savingSettings, setSavingSettings] = useState(false);

  const isAdmin = canPerform('update_org');

  const toggleUrlImport = async () => {
    if (!isAdmin) return;
    setSavingSettings(true);
    try {
      const next = !(org?.settings?.urlImportEnabled);
      const res = await authFetch('/api/orgs/me/settings', {
        method: 'PATCH',
        body: JSON.stringify({ urlImportEnabled: next }),
      });
      if (res.ok) await refresh();
    } catch { /* ignore */ } finally {
      setSavingSettings(false);
    }
  };

  const updateIdentityRevealMode = async (mode: 'always_visible' | 'optional_toggle' | 'always_masked') => {
    if (!isAdmin) return;
    setSavingSettings(true);
    try {
      const res = await authFetch('/api/orgs/me/settings', {
        method: 'PATCH',
        body: JSON.stringify({ identityRevealMode: mode }),
      });
      if (res.ok) await refresh();
    } catch {
      // ignore
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="v2-page" style={{ maxWidth: 800 }}>
      <div className="v2-page-header">
        <h1>Settings</h1>
        <p>Manage your organization, billing, and preferences</p>
      </div>

      {/* Organization */}
      <div className="v2-card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="v2-card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <IconBuilding />
            <h2>Organization</h2>
          </div>
          <a href="#/organization" className="v2-btn v2-btn-ghost">Manage</a>
        </div>
        <div className="v2-card-body">
          {org ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', marginBottom: 2 }}>Name</div>
                <div style={{ fontWeight: 600 }}>{org.name}</div>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', marginBottom: 2 }}>Your Role</div>
                <div><span className="v2-badge v2-badge-primary">{role}</span></div>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', marginBottom: 2 }}>Plan</div>
                <div style={{ fontWeight: 600 }}>{org.plan}</div>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', marginBottom: 2 }}>Members</div>
                <div style={{ fontWeight: 600 }}>{org.members?.length ?? 1}</div>
              </div>
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>Loading organization info...</p>
          )}
        </div>
      </div>

      {/* Billing / Usage */}
      <div className="v2-card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="v2-card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <IconCreditCard />
            <h2>Usage & Billing</h2>
          </div>
          <a href="#/billing" className="v2-btn v2-btn-ghost">Details</a>
        </div>
        <div className="v2-card-body">
          {usage ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-4)' }}>
              <UsageBar label="Uploads" used={usage.usage.resumeUploads.used} total={usage.usage.resumeUploads.monthly} />
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>Loading usage data...</p>
          )}
        </div>
      </div>

      {/* Dimension Standards */}
      <div className="v2-card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="v2-card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <IconLayers />
            <h2>Dimension Standards</h2>
          </div>
          <a href="#/settings/dimension-standards" className="v2-btn v2-btn-ghost">Read</a>
        </div>
        <div className="v2-card-body">
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
            Review how xRank dimensions map to O*NET and SHRM-aligned competency taxonomy.
          </p>
        </div>
      </div>

      {/* Developer */}
      <div className="v2-card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="v2-card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <IconToggle />
            <h2>Preferences</h2>
          </div>
        </div>
        <div className="v2-card-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
            <div>
              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 2 }}>
                URL Import for Job Descriptions
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                Show the "Import URL" tab when creating jobs. Works with Greenhouse, Lever, Workday, and company career pages.
              </div>
            </div>
            <button
              onClick={toggleUrlImport}
              disabled={!isAdmin || savingSettings}
              style={{
                width: 44,
                height: 24,
                borderRadius: 12,
                border: 'none',
                padding: 2,
                cursor: isAdmin ? 'pointer' : 'not-allowed',
                background: org?.settings?.urlImportEnabled ? 'var(--accent-color)' : 'var(--border-color)',
                transition: 'background 0.2s',
                flexShrink: 0,
                opacity: savingSettings ? 0.5 : 1,
              }}
              title={isAdmin ? 'Toggle URL import' : 'Admin permission required'}
            >
              <div style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: 'white',
                transition: 'transform 0.2s',
                transform: org?.settings?.urlImportEnabled ? 'translateX(20px)' : 'translateX(0)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </button>
          </div>

          <div style={{ marginTop: 'var(--space-4)', borderTop: '1px solid var(--border-color)', paddingTop: 'var(--space-4)' }}>
            <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 2 }}>
              Identity Reveal Policy
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
              Controls candidate identity exposure and resume preview modes for everyone in this organization.
            </div>
            <select
              value={org?.settings?.identityRevealMode || 'always_visible'}
              onChange={(e) => updateIdentityRevealMode(e.target.value as 'always_visible' | 'optional_toggle' | 'always_masked')}
              disabled={!isAdmin || savingSettings}
              style={{
                minWidth: 260,
                padding: '8px 10px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontSize: 'var(--font-size-sm)',
              }}
              title={isAdmin ? 'Set identity reveal policy' : 'Admin permission required'}
            >
              <option value="always_visible">Always reveal (original only)</option>
              <option value="optional_toggle">Optional toggle (masked by default)</option>
              <option value="always_masked">Always masked (redacted only)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Developer */}
      <div className="v2-card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="v2-card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <IconCode />
            <h2>Developer</h2>
          </div>
          <a href="#/developer" className="v2-btn v2-btn-ghost">API Docs</a>
        </div>
        <div className="v2-card-body">
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
            Access API documentation, view request logs, and manage keys.
          </p>
        </div>
      </div>
    </div>
  );
};

function UsageBar({ label, used, total }: { label: string; used: number; total: number }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 'var(--font-size-xs)' }}>
        <span style={{ color: 'var(--text-muted)' }}>{label}</span>
        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{used}/{total}</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-card)', overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          borderRadius: 3,
          background: pct > 90 ? 'var(--color-error)' : pct > 70 ? 'var(--color-warning)' : 'var(--color-primary)',
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
}
